/**
 * 视频记录（Alice 页面 videos 的复刻）：异步生成任务卡片墙、结算结果、人工审核。
 *
 * 背景：数据来自控制面 /control/admin/video-logs：进行中的任务取自 Redis，已结算的取自用量记录，审核状态取自
 *   media_reviews。Alice 的网盘占用、存储状态、归档状态在本仓没有对应物（没有网盘与归档副本），对应的筛选与
 *   角标不显示，第三张指标卡换成「违规 / 疑似」；成片地址是上游直链。
 * 设计意图：卡片的 DOM、类名、字段顺序与 Alice 一致；有进行中的任务时每 10 秒静默刷新一次（每轮只请求当前页，
 *   工作量固定），没有进行中的任务就不轮询；审核动作用确认弹窗，成功后刷新当前页。
 * 关键约束：审核只对已成功的视频开放；写操作要 admin 角色，support 点击会得到 403 提示。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReviewAction, VideoLogs, VideoRecord } from '@ap/api-client/admin-runtime2'
import { runtime2Api } from '@ap/api-client/admin-runtime2'
import { AG_runtime2 as G, errorText2 } from '@ap/i18n/admin-runtime2'
import { useI18n } from '@ap/i18n'
import { ConfirmModal, fmtDateTime, useToast } from '@ap/ui'
import { OpsNote, OpsPage, Stat, Stats, Toolbar } from '../../alice/kit.tsx'
import { PILL, PageNav, Pill, Section, ToolInput, ToolSelect, ellipsis, type PillTone } from '../../alice/kit-runtime2.tsx'
import { useUsd } from '../../shared.tsx'

const PAGE = 18
const POLL_MS = 10_000

interface Filters {
  start: string
  end: string
  user: string
  model: string
  prompt: string
  status: string
  review: string
}

const EMPTY: Filters = { start: '', end: '', user: '', model: '', prompt: '', status: 'all', review: 'all' }

const STATUS_TONE: Record<string, PillTone> = {
  submitting: PILL.blue,
  queued: PILL.purple,
  running: PILL.amber,
  succeeded: PILL.green,
  failed: PILL.red,
  cancelled: PILL.gray,
}

const REVIEW_TONE: Record<string, PillTone> = {
  pending: PILL.blue,
  passed: PILL.green,
  suspect: PILL.amber,
  violation: PILL.red,
  failed: PILL.gray,
  skipped: PILL.gray,
}

export function Videos() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const usd = useUsd()
  const [draft, setDraft] = useState<Filters>(EMPTY)
  const [applied, setApplied] = useState<Filters>(EMPTY)
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState<VideoLogs | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)
  const [pending, setPending] = useState<{ id: string; action: ReviewAction } | null>(null)
  const [busy, setBusy] = useState(false)
  const quiet = useRef(false)
  const seq = useRef(0)

  useEffect(() => {
    const my = ++seq.current
    if (!quiet.current) setLoading(true)
    quiet.current = false
    runtime2Api
      .videoLogs({
        user: applied.user.trim(),
        model: applied.model.trim(),
        prompt: applied.prompt.trim(),
        status: applied.status,
        review_status: applied.review,
        start_date: applied.start,
        end_date: applied.end ? `${applied.end} 23:59:59` : undefined,
        limit: PAGE,
        offset,
      })
      .then((d) => {
        if (my !== seq.current) return
        setData(d)
        setError(null)
      })
      .catch((e) => {
        if (my !== seq.current) return
        console.error('[videos] 加载失败', e)
        setError(e)
      })
      .finally(() => {
        if (my === seq.current) setLoading(false)
      })
  }, [applied, offset, tick])

  // 有进行中的任务时才轮询：每轮只刷新当前页
  const active = data?.summary.active ?? 0
  useEffect(() => {
    if (active <= 0) return
    const id = window.setTimeout(() => {
      quiet.current = true
      setTick((n) => n + 1)
    }, POLL_MS)
    return () => window.clearTimeout(id)
  }, [active, data])

  const apply = useCallback((next: Filters) => {
    setDraft(next)
    setApplied(next)
    setOffset(0)
  }, [])
  const setField = (patch: Partial<Filters>, immediate = false) => {
    const next = { ...draft, ...patch }
    if (immediate) apply(next)
    else setDraft(next)
  }

  const review = async () => {
    if (!pending) return
    setBusy(true)
    try {
      const r = await runtime2Api.reviewVideo(pending.id, pending.action)
      toast.ok(t(r.enforced ? G.videos.updated : G.videos.notEnforced))
      setPending(null)
      quiet.current = true
      setTick((n) => n + 1)
    } catch (e) {
      toast.error(errorText2(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const s = data?.summary
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))
  const page = Math.floor(offset / PAGE) + 1
  const confirmText = pending ? t({ pass: G.videos.confirmPass, block: G.videos.confirmBlock, retry: G.videos.confirmRetry }[pending.action]) : ''

  return (
    <OpsPage eyebrow={t(G.videos.eyebrow)} title={t(G.videos.title)} desc={t(G.videos.desc)} chip={t(G.videos.chip)}>
      <Stats>
        <Stat
          label={t(G.videos.statTotal)}
          value={s ? total.toLocaleString() : '—'}
          sub={s ? t(G.videos.statTotalSub, { ok: s.succeeded, active: s.active, failed: s.failed }) : undefined}
        />
        <Stat label={t(G.videos.statSeconds)} value={s ? t(G.common.seconds, { n: s.requested_seconds.toLocaleString() }) : '—'} sub={t(G.videos.statSecondsSub)} />
        <Stat
          label={t(G.videos.statReview)}
          value={s ? `${s.review_violation} / ${s.review_suspect}` : '—'}
          color={s && s.review_violation > 0 ? 'error' : undefined}
          sub={s ? t(G.videos.statReviewSub, { n: s.review_pending }) : undefined}
        />
        <Stat
          label={t(G.videos.statCharge)}
          value={s ? `${usd(s.user_cost_units, 4)} / ${usd(s.margin_units, 4)}` : '—'}
          sub={s ? t(G.videos.statChargeSub, { m: usd(s.margin_units, 4), n: s.known_cost_tasks }) : undefined}
        />
      </Stats>

      <Toolbar
        count={s ? t(G.videos.totalBadge, { total: total.toLocaleString(), v: s.review_violation, s: s.review_suspect, p: s.review_pending }) : undefined}
      >
        <ToolInput type="date" aria-label={t(G.videos.start)} value={draft.start} onChange={(e) => setField({ start: e.target.value }, true)} />
        <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{t(G.common.to)}</span>
        <ToolInput type="date" aria-label={t(G.videos.end)} value={draft.end} onChange={(e) => setField({ end: e.target.value }, true)} />
        <ToolInput width={190} placeholder={t(G.videos.userPh)} value={draft.user} onChange={(e) => setField({ user: e.target.value })} onEnter={() => apply(draft)} />
        <ToolInput width={140} placeholder={t(G.videos.modelPh)} value={draft.model} onChange={(e) => setField({ model: e.target.value })} onEnter={() => apply(draft)} />
        <ToolInput width={170} placeholder={t(G.videos.promptPh)} value={draft.prompt} onChange={(e) => setField({ prompt: e.target.value })} onEnter={() => apply(draft)} />
        <ToolSelect
          value={draft.status}
          onChange={(e) => setField({ status: e.target.value }, true)}
          options={[{ value: 'all', label: t(G.videos.statusAll) }, ...Object.entries(G.videos.status).map(([value, entry]) => ({ value, label: t(entry) }))]}
        />
        <ToolSelect
          value={draft.review}
          onChange={(e) => setField({ review: e.target.value }, true)}
          options={[
            { value: 'all', label: t(G.videos.reviewAll) },
            { value: 'flagged', label: t(G.videos.reviewFlagged) },
            ...Object.entries(G.videos.reviewOptions).map(([value, entry]) => ({ value, label: t(entry) })),
          ]}
        />
        <button className="btn btn-secondary btn-sm" onClick={() => apply(draft)}>
          {t(G.common.filter)}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => apply(EMPTY)}>
          {t(G.common.clear)}
        </button>
      </Toolbar>
      <OpsNote>{t(G.videos.note)}</OpsNote>

      <Section title={t(G.videos.sectionTitle)} copy={t(G.videos.sectionCopy)}>
        <div id="videosGrid">
          {loading && !data ? (
            <div className="empty" style={{ gridColumn: '1/-1', padding: 40 }}>
              {t(G.common.loading)}
            </div>
          ) : error ? (
            <div className="empty" style={{ gridColumn: '1/-1', padding: 40, color: 'var(--error-text)' }}>
              {t(G.common.loadFailed, { msg: errorText2(error, locale) })}
            </div>
          ) : data && data.records.length ? (
            data.records.map((r) => <VideoCard key={r.request_id} r={r} onReview={(action) => setPending({ id: r.request_id, action })} />)
          ) : (
            <div className="empty" style={{ gridColumn: '1/-1', padding: 40 }}>
              {t(G.videos.empty)}
            </div>
          )}
        </div>
        <div style={{ padding: '4px 16px 16px', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
          {total > 0 && (
            <PageNav
              page={page}
              pages={pages}
              label={t(G.common.pageOf, { p: page, n: pages })}
              prev={t(G.common.prev)}
              next={t(G.common.next)}
              onPage={(p) => setOffset((p - 1) * PAGE)}
            />
          )}
        </div>
      </Section>

      <ConfirmModal
        open={pending !== null}
        title={pending ? t({ pass: G.videos.actionPass, block: G.videos.actionBlock, retry: G.videos.actionRetry }[pending.action]) : ''}
        danger={pending?.action === 'block'}
        busy={busy}
        onConfirm={review}
        onClose={() => setPending(null)}
      >
        {confirmText}
      </ConfirmModal>
    </OpsPage>
  )
}

/** 单张卡片放在模块层：放进 Videos 里会在每次渲染重建，播放中的视频会被打断。 */
function VideoCard({ r, onReview }: { r: VideoRecord; onReview: (a: ReviewAction) => void }) {
  const { t, locale } = useI18n()
  const usd = useUsd()
  const statusTone = STATUS_TONE[r.status] ?? PILL.gray
  const succeeded = r.status === 'succeeded'
  const reviewTone = succeeded ? (REVIEW_TONE[r.review_status] ?? PILL.gray) : PILL.gray
  const reviewLabel = !succeeded && r.review_status === 'pending' ? G.videos.review.noFile : G.videos.review[r.review_status]
  const note = r.review_error || r.review_reason
  const user = r.account_name || t(G.common.accountFallback, { id: r.account_id.slice(0, 8) })
  const prompt = r.prompt || t(G.videos.noPrompt)
  const charge = r.user_cost_units == null ? t(G.common.unsettled) : usd(r.user_cost_units, 4)
  const upstream = r.upstream_cost_units == null ? t(G.common.costUnknown) : t(G.common.cost, { v: usd(r.upstream_cost_units, 4) })
  return (
    <article className="video-record-card">
      {r.play_url ? (
        <video controls preload="metadata" src={r.play_url} />
      ) : (
        <div className="video-placeholder">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="4" width="15" height="16" rx="2" />
            <path d="m17 9 5-3v12l-5-3z" />
            <path d="m7 9 4 3-4 3z" />
          </svg>
          <span style={{ fontSize: 12 }}>{t(G.videos.noFile)}</span>
        </div>
      )}
      <div style={{ padding: '12px 13px 13px' }}>
        <div className="video-meta-row">
          <Pill tone={statusTone}>{t(G.videos.status[r.status] ?? G.videos.status.failed)}</Pill>
          <span>{fmtDateTime(r.created_at, locale)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 10 }}>
          <span className="mono" title={`account: ${r.account_id}`} style={{ fontSize: 11, fontWeight: 650 }}>
            {user}
          </span>
          {r.end_user && (
            <span className="mono" style={{ fontSize: 10, color: 'var(--text-tertiary)', ...ellipsis }} title={r.end_user}>
              {r.end_user}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 8 }}>
          <Pill tone={reviewTone} title={note}>
            {t(reviewLabel)}
            {r.review_source === 'admin' ? t(G.common.manual) : ''}
          </Pill>
        </div>
        {note && (
          <div title={note} style={{ marginTop: 5, fontSize: 10, color: 'var(--text-secondary)', ...ellipsis }}>
            {note}
          </div>
        )}
        <div className="video-prompt" title={prompt}>
          {prompt}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 10, fontSize: 10 }}>
          <span className="mono" style={{ color: 'var(--primary)', ...ellipsis }} title={r.model_id}>
            {r.model_id}
          </span>
          <span style={{ whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
            {r.resolution} · {t(G.common.seconds, { n: r.duration })}
          </span>
        </div>
        <div className="video-meta-row" style={{ marginTop: 8 }}>
          <span>
            {charge} · {upstream}
          </span>
          {r.play_url && (
            <a href={r.play_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
              {t(G.videos.openNew)}
            </a>
          )}
        </div>
        {r.error_message && (
          <div
            title={r.error_message}
            style={{ marginTop: 8, padding: '7px 8px', borderRadius: 6, background: 'var(--error-bg)', color: 'var(--error-text)', fontSize: 10, lineHeight: 1.45, ...ellipsis }}
          >
            {r.error_message}
          </div>
        )}
        {succeeded && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {r.review_status === 'violation' ? (
              <button className="btn btn-secondary btn-sm" onClick={() => onReview('pass')}>
                {t(G.videos.actionPass)}
              </button>
            ) : (
              <button className="btn btn-secondary btn-sm" onClick={() => onReview('block')}>
                {t(G.videos.actionBlock)}
              </button>
            )}
            {r.review_status !== 'pending' && (
              <button className="btn btn-secondary btn-sm" onClick={() => onReview('retry')}>
                {t(G.videos.actionRetry)}
              </button>
            )}
          </div>
        )}
        <div className="mono" title={r.request_id} style={{ marginTop: 8, color: 'var(--text-tertiary)', fontSize: 9, ...ellipsis }}>
          {r.task_id || r.request_id}
        </div>
      </div>
    </article>
  )
}
